import React from 'react';
import { LuShieldCheck, LuShieldAlert, LuShieldX, LuShieldQuestion } from 'react-icons/lu';
import SectionCard from './SectionCard';
import { RUN_OUTCOME, fmtCount, fmtDateTime, fmtPct, runPath } from './format';

const STATUS = {
  ready: { label: 'Ready', tone: 'success', icon: LuShieldCheck },
  at_risk: { label: 'At risk', tone: 'warning', icon: LuShieldAlert },
  blocked: { label: 'Blocked', tone: 'danger', icon: LuShieldX },
  not_evaluated: { label: 'Not evaluated', tone: 'neutral', icon: LuShieldQuestion },
  not_configured: { label: 'Not configured', tone: 'neutral', icon: LuShieldQuestion },
};

function Signal({ label, value, tone = 'neutral', action }) {
  return (
    <li className="pqd-signal">
      <span className={`rt-status-dot rt-status-dot--${tone}`} aria-hidden="true" />
      <span className="pqd-signal__label">{label}</span>
      <span className="pqd-signal__value">{value}</span>
      {action}
    </li>
  );
}

export default function ReleaseReadiness({ section, navigateTo, onOpenFailures, projectId }) {
  const { data, loading, error, retry, refreshing } = section;
  const status = STATUS[data?.status] || STATUS.not_evaluated;
  const Icon = status.icon;
  const s = data?.signals || {};
  const run = s.latest_run;
  const runOutcome = RUN_OUTCOME[run?.outcome] || RUN_OUTCOME.unknown;
  const hp = s.high_priority_failing || { count: 0, items: [] };

  return (
    <SectionCard
      id="pqd-readiness"
      title="Release readiness"
      subtitle={data?.evaluated_at ? `Checked ${fmtDateTime(data.evaluated_at)}` : undefined}
      loading={loading}
      error={error}
      onRetry={retry}
      refreshing={refreshing}
    >
      {data && (
        <div className="pqd-readiness">
          <div className={`pqd-readiness__status pqd-readiness__status--${status.tone}`}>
            <Icon size={20} aria-hidden="true" />
            <div>
              <strong>{status.label}</strong>
              <p>{data.reason}</p>
            </div>
          </div>

          <h3 className="pqd-subheading">Signals for your release decision</h3>
          <ul className="pqd-signals">
            <Signal
              label="Latest pass rate"
              value={s.pass_rate?.value == null ? 'No results' : `${fmtPct(s.pass_rate.value)} of ${fmtCount(s.pass_rate.evaluated)}`}
            />
            <Signal
              label="Failing tests"
              value={s.failing_tests == null ? 'No results' : fmtCount(s.failing_tests)}
              tone={s.failing_tests ? 'danger' : s.failing_tests === 0 ? 'success' : 'neutral'}
              action={
                s.failing_tests ? (
                  <button type="button" className="rt2-link" onClick={() => onOpenFailures('failing')}>Review</button>
                ) : null
              }
            />
            <Signal
              label="High/critical priority failing"
              value={fmtCount(hp.count)}
              tone={hp.count ? 'danger' : 'success'}
            />
            <Signal
              label="Flaky tests (30 days)"
              value={fmtCount(s.flaky_tests)}
              tone={s.flaky_tests ? 'warning' : 'success'}
              action={
                s.flaky_tests ? (
                  <button type="button" className="rt2-link" onClick={() => onOpenFailures('flaky')}>Review</button>
                ) : null
              }
            />
            <Signal
              label="Latest run"
              value={run ? `${runOutcome.label} · ${run.name}` : 'No runs yet'}
              tone={run ? runOutcome.tone : 'neutral'}
              action={
                run ? (
                  <button type="button" className="rt2-link" onClick={() => navigateTo(runPath(run))}>Open</button>
                ) : null
              }
            />
          </ul>
          {hp.items?.length > 0 && (
            <ul className="pqd-inline-list" aria-label="High priority failing tests">
              {hp.items.map((t) => (
                <li key={t.test_case_id}>
                  <button
                    type="button"
                    className="rt2-link"
                    onClick={() => navigateTo(`test-cases/${t.test_case_id}?project_id=${projectId}`)}
                  >
                    {t.name || `Test case #${t.test_case_id}`}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SectionCard>
  );
}
